/**
 * Параметризуемое ядро прямой формулы цены — единое для бэкенда и фронтенда.
 *
 * Все 5 калькуляторов с обеих сторон (calcPrice/calcPrice2/calcOzonPrice/calcYandexPrice/calcPriceWithSizes
 * на бэке + defaultCalcPrice/defaultCalcPrice2/calcPriceForOzon/calcPriceForYandex/calcPriceWithSizes на фронте)
 * сводятся к одной и той же структуре:
 *
 *   PRIBL    = max(MARZA * price, MIN_CONST_PRIBL)
 *   KASSA    = (PRIBL + FF + price) / (1 - NALOG)   — если NALOG применяется к кассе
 *            | PRIBL + FF + price                    — если NALOG применяется в знаменателе
 *   current  = ceil((KASSA + additives...) / (1 - denominatorFees...))
 *   result   = withSale ? ceil(current / (1 - SALE)) : current
 *
 * Различия между калькуляторами — это:
 *  1. Где применяется NALOG: в KASSA (default2/ozon/yandex) или в знаменателе вместе с KOEF (default1/withSizes).
 *  2. Какие комиссии входят в знаменатель (только KOEF; KOEF+NALOG; KOEF+EKV+MILA; KOEF+EKV+LOGISTIC).
 *  3. Какие добавки прибавляются к KASSA перед делением (LOGISTIC; LOGISTIC+PRIEMKA; MILA+PRIEMKA).
 *
 * Движок параметризуется конфигом комиссий, что позволяет сохранить математическое
 * различие default1 vs default2 (NALOG в знаменателе vs в KASSA) и при этом убрать
 * дублирование каркаса на обеих сторонах.
 */
import {calcProfit, applySale} from "./helpers";

export interface PriceFromCostFees {
  // Применяется ли NALOG к KASSA (true → KASSA = (PRIBL+FF+price)/(1-NALOG)),
  // или NALOG остаётся в знаменателе (false → KASSA = PRIBL+FF+price).
  nalogInKassa: boolean;
  // Комиссии, вычитаемые в знаменателе: currentPrice = (KASSA + additives) / (1 - sum(denominatorFees))
  denominatorFees: number[];
  // Добавки к KASSA перед делением (LOGISTIC, PRIEMKA, MILA — зависит от калькулятора)
  additives: number[];
}

export interface PriceFromCostInput {
  price: number;
  margin: number;
  minProfit: number;
  ff: number;
  nalog: number;
  sale: number;
  withSale: boolean;
}

/**
 * Прямой расчёт цены по параметрам и конфигу комиссий.
 *
 * Возвращает currentPrice с применённой скидкой (если withSale=true).
 * Все округления (Math.ceil для цены и для скидки) сохранены как в оригиналах.
 *
 *_sale_ здесь — доля (0.02 = 2%), НЕ проценты. Фронт, historically передаёт sale
 * в процентах (напр. 2 для 2%) — вызывающий код должен поделить на 100 перед вызовом
 * либо использовать адаптер.
 */
export const priceFromCost = (input: PriceFromCostInput, fees: PriceFromCostFees): number => {
  const PRIBL = calcProfit(input.margin, input.price, input.minProfit);
  const baseKassa = PRIBL + input.ff + input.price;
  const KASSA = fees.nalogInKassa ? baseKassa / (1 - input.nalog) : baseKassa;
  const additivesSum = fees.additives.reduce((acc, x) => acc + x, 0);
  const denominator = 1 - fees.denominatorFees.reduce((acc, x) => acc + x, 0);
  if (!Number.isFinite(denominator) || denominator <= 0) {
    return input.price;
  }
  const currentPrice = Math.ceil((KASSA + additivesSum) / denominator);
  return input.withSale ? applySale(currentPrice, input.sale) : currentPrice;
};
